import { Billing, CreateSubscriptionOutput, GetPlanOutput, errors as BillingErrors } from "@smash-sdk/billing";
import { config, Region } from '@smash-sdk/core';
import { Directory } from "@smash-sdk/directory/ejs/10-2019";
import { Iam } from "@smash-sdk/iam/ejs/10-2019";
import { Iam as IamV102025 } from "@smash-sdk/iam/ejs/10-2025/iam";
import jwt_decode from "jwt-decode";
import moment from 'moment';
import { getDecodedToken, isApiPaidPlan } from "../../../shared/helpers/helpers";
import { DisplayUser } from "../models/DisplayUser.interface";
import { SignupUser } from "../models/SignupUser.interface";
import cookieService from "./cookie.service";
import { Kyc, errors as KycErrors } from "@smash-sdk/kyc";
import { VerificationEmail } from "../authSlice";
import { emailStatuses } from "../models/EmailStatus.interface";


const hasActiveSession = (): boolean => {
    const cookieToken = cookieService.get(process.env.REACT_APP_TOKEN_NAME as string);
    if (cookieToken) {
        config.setToken(cookieToken);
        const decodedToken = getDecodedToken();
        if (decodedToken.username) {
            return true;
        }
    }
    return false;
};

const createAnonymousUser = async (): Promise<{ identity: DisplayUser | null }> => {
    try {
        const iamSdk = new Iam();
        const { account } = await iamSdk.createAccount();
        setIdentity({ identity: account });
        return { identity: account }
    } catch (error) {
        throw error;
    }
}

const createSubscriptionFreeTrial = async () => {
    try {
        const billing = new Billing();
        const { group } = await billing.getGroup({ groupId: process.env.REACT_APP_SMASH_PLAN_FREE_TRIAL_GROUP_ID as string });
        if (!group?.plans?.length) {
            throw new Error(`No plan found for groupId ${process.env.REACT_APP_SMASH_PLAN_FREE_TRIAL_GROUP_ID}`);
        }
        const freeTrialPlan = group.plans[0]; // TODO FIX this ??
        const { plan } = await billing.getPlan({ planId: freeTrialPlan.id });
        const { subscription } = await billing.createSubscription({ plan: freeTrialPlan.id });
        return { subscription, plan };
    } catch (error) {
        throw error;
    }

}

const signup = async (userSignup: SignupUser): Promise<{ identity: DisplayUser | null, emailStatus?: VerificationEmail['emailStatus'], subscription: CreateSubscriptionOutput['subscription'] | null, plan: GetPlanOutput['plan'] | null }> => {
    const iamSdk = new Iam();
    const { account } = await iamSdk.createAccount({ ...userSignup });
    const directory = new Directory();
    const { identity } = await directory.createToken({ ...userSignup });
    try {
        if (account && identity) {
            setIdentity({ identity, emailStatus: emailStatuses.NotVerified });
            const kyc = new Kyc();
            await kyc.createEmailChallenge({ email: identity.username });

            const { subscription, plan } = await createSubscriptionFreeTrial();

            return { identity, emailStatus: emailStatuses.Verified, subscription, plan };
        }
        return { identity: null, subscription: null, plan: null }
    } catch (error) {
        if (error instanceof BillingErrors.CreateSubscriptionError.EmailNotVerifiedError) {
            setIdentity({ identity, emailStatus: emailStatuses.NotVerified });
            return { identity, emailStatus: emailStatuses.NotVerified, subscription: null, plan: null };
        }
        logout();
        throw error
    }
}

const sendCode = async () => {
    const identity = localStorage.getItem('identity');
    if (identity) {
        const parsedIdentity = JSON.parse(identity) as DisplayUser;
        const { username, token, refreshToken, session } = parsedIdentity;
        if (username && token && (refreshToken || session)) {
            const kyc = new Kyc();
            await kyc.createEmailChallenge({ email: username });
        }
    }
}

/**
 * Exchange the authorization code issued by the StaticSmashAuth portal for an
 * identity, then apply the same subscription/email-status gating the legacy
 * password login used. The region is derived from the code itself so the IAM
 * SDK targets the right regional endpoint before the identity is known.
 *
 * @param authorizationCode Authorization code returned by the portal (`authCode`).
 * @returns The identity + resolved email status, or `{ identity: null }`.
 * @throws "notCompatible" when the account is not on a paid API plan; propagates
 *         IAM/billing errors. Storage is cleared on failure.
 */
const verifyAuthorizationCode = async (authorizationCode: string): Promise<{ identity: DisplayUser | null, emailStatus?: VerificationEmail['emailStatus'] }> => {
    const { region } = jwt_decode<{ region: string }>(authorizationCode);
    // Set the region on the shared config so the SDK resolves the discovery-configured host
    // for that region (dev/prod), instead of falling back to the default prod host.
    config.setRegion(region as Region);
    const iam = new IamV102025();
    const { identity } = await iam.verifyAuthorizationCode({ authorizationCode });
    const displayUser = identity as unknown as DisplayUser;
    try {
        config.setToken(identity.token.token);
        config.setRegion((identity.region ?? region) as Region);
        const billing = new Billing();
        const { subscription } = await billing.getSubscription();

        if (!isApiPaidPlan(subscription.plan)) {
            throw new Error(`notCompatible`);
        }

        if (identity?.region && subscription.status !== 'Inactive') {
            setIdentity({ identity: displayUser, emailStatus: emailStatuses.Verified });
            return { identity: displayUser, emailStatus: emailStatuses.Verified };
        } else if (identity?.region) {
            setIdentity({ identity: displayUser, emailStatus: emailStatuses.NotVerified });
            return { identity: displayUser, emailStatus: emailStatuses.NotVerified };
        }

        return { identity: null }
    } catch (error) {
        logout();
        throw error;
    }
}

const logout = (): void => {
    localStorage.removeItem('identity');
    localStorage.removeItem('emailStatus');
    cookieService.remove(process.env.REACT_APP_TOKEN_NAME as string);
    cookieService.remove(process.env.REACT_APP_REFRESH_TOKEN_NAME as string);
}

const verifyJwt = async (): Promise<boolean> => {
    const identity = localStorage.getItem('identity');
    const emailStatus = localStorage.getItem('emailStatus');
    if (identity && emailStatus) {
        const parsedIdentity = JSON.parse(identity) as DisplayUser;
        const parsedEmailStatus = JSON.parse(emailStatus) as VerificationEmail;
        const { username, token, refreshToken, session } = parsedIdentity;
        // Accept both legacy (refresh-token) and portal (session-bound) identities.
        if (username && token && (refreshToken || session)) {
            setIdentity({ identity: parsedIdentity, emailStatus: parsedEmailStatus.emailStatus });
            return true;
        }
    }
    return false;
}

const verifyEmail = async (code: number): Promise<{ identity: DisplayUser | null, emailStatus?: VerificationEmail['emailStatus'] }> => {
    const identity = localStorage.getItem('identity');
    if (identity) {
        const parsedIdentity = JSON.parse(identity) as DisplayUser;
        const { username, token, refreshToken, session } = parsedIdentity;
        if (username && token && (refreshToken || session)) {
            const kyc = new Kyc();
            try {
                const { emailChallenge } = await kyc.updateEmailChallenge({ email: username, token: code });
                if (emailChallenge.status === "Clean") {
                    await createSubscriptionFreeTrial();
                    setIdentity({ identity: parsedIdentity, emailStatus: emailStatuses.Verified });
                    return { identity: parsedIdentity, emailStatus: emailStatuses.Verified };
                } else {
                    setIdentity({ identity: parsedIdentity, emailStatus: emailStatuses.NotVerified });
                    return { identity: parsedIdentity, emailStatus: emailStatuses.NotVerified };
                }
            } catch (error) {
                if (error instanceof KycErrors.UpdateEmailChallengeError.InvalidTokenError) {
                    error.message = 'Incorrect code'
                    throw error
                }
                throw error;
            }
        }
    }
    return { identity: null };
}

const setIdentity = ({ identity, emailStatus }: { identity: DisplayUser, emailStatus?: VerificationEmail['emailStatus'] }) => {
    const { token, refreshToken } = identity;
    cookieService.set(process.env.REACT_APP_TOKEN_NAME as string, token.token, { expires: moment(token.expiration).toDate() });
    // Portal (session-bound) identities carry no refresh token; only set the cookie when present.
    if (refreshToken) {
        cookieService.set(process.env.REACT_APP_REFRESH_TOKEN_NAME as string, refreshToken.token, { expires: moment(refreshToken.expiration).toDate() });
    }
    localStorage.setItem('identity', JSON.stringify(identity));
    localStorage.setItem('emailStatus', JSON.stringify({ emailStatus }));
    config.setToken(token.token);
}

const authService = {
    hasActiveSession,
    createAnonymousUser,
    signup,
    verifyAuthorizationCode,
    logout,
    verifyJwt,
    verifyEmail,
    setIdentity,
    sendCode
}

export default authService;